/** Browser speech, the stand-in for the phone's TTS. Same voice preferences as the review page. */

export class Speech {
  private voices: SpeechSynthesisVoice[] = [];
  private current: SpeechSynthesisUtterance | null = null;
  readonly available = typeof speechSynthesis !== 'undefined';

  constructor(private select: HTMLSelectElement, initialVoice: string) {
    if (!this.available) return;
    const load = (): void => {
      this.voices = speechSynthesis.getVoices().filter((v) => /^en/i.test(v.lang));
      if (!this.voices.length) this.voices = speechSynthesis.getVoices();
      this.select.innerHTML = this.voices.map((v, i) => `<option value="${i}">${v.name.replace(/\s*\(.*\)$/, '')}</option>`).join('');
      const wanted = this.voices.findIndex((v) => v.name === initialVoice);
      // The house voice is Chrome's Google UK English Male; otherwise the most natural one on offer.
      const house = this.voices.findIndex((v) => /google uk english male/i.test(v.name));
      const better = this.voices.findIndex((v) => /natural|neural|enhanced|premium|google/i.test(v.name));
      this.select.value = String(wanted >= 0 ? wanted : house >= 0 ? house : better >= 0 ? better : 0);
    };
    load();
    speechSynthesis.onvoiceschanged = load;
  }

  get voiceName(): string {
    return this.voices[Number(this.select.value)]?.name ?? '';
  }

  /** Rough spoken length, used to pace the car before the engine reports the real end. */
  estimateSec(text: string, rate: number): number {
    return text.trim().split(/\s+/).length / (2.6 * rate);
  }

  speak(text: string, rate: number, onEnd: () => void): void {
    if (!this.available) {
      onEnd();
      return;
    }
    this.stop();
    const u = new SpeechSynthesisUtterance(text);
    const v = this.voices[Number(this.select.value)];
    if (v) {
      u.voice = v;
      u.lang = v.lang;
    }
    u.rate = rate;
    const done = (): void => {
      if (this.current === u) {
        this.current = null;
        onEnd();
      }
    };
    u.onend = done;
    u.onerror = done;
    this.current = u;
    speechSynthesis.speak(u);
  }

  stop(): void {
    if (!this.available) return;
    this.current = null;
    speechSynthesis.cancel();
  }
}
